import { useEffect, useId, useRef, useState } from 'react';
import { Volume2, VolumeX, Music, Bell } from 'lucide-react';
import { useSoundPrefs, setSoundPrefs, type SoundPrefs } from '../lib/sounds';

// The speaker button in every header (inside a `relative` .hdr-group). Tapping it opens a small card with two
// separate switches: Ambient music and Notification sounds.
// `buttonClassName` keeps each header's own button look.
const ROWS: { key: keyof SoundPrefs; label: string; Icon: typeof Music }[] = [
  { key: 'ambient', label: 'Ambient music', Icon: Music },
  { key: 'notifications', label: 'Notification sounds', Icon: Bell },
];

export default function SoundMenu({ isNight, buttonClassName }: { isNight: boolean; buttonClassName: string }) {
  const prefs = useSoundPrefs();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const allOff = !prefs.ambient && !prefs.notifications;

  // Close on a tap outside, or Escape (focus goes back to the button).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    // Move focus to the first switch, so keyboard users land inside.
    wrapRef.current?.querySelector<HTMLButtonElement>('[role="switch"]')?.focus();
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const ring = isNight ? 'focus-visible:ring-white/70' : 'focus-visible:ring-black/60';
  return (
    // Not "relative": the card lines up with the right edge of the whole
    // header button row (.hdr-group), so it never covers the back arrow.
    <div ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Sound settings${allOff ? ', all sound off' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(o => !o)}
        className={`${buttonClassName} outline-none focus-visible:ring-2 ${ring}`}
      >
        {allOff ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {open && (
        // Outer layer floats under the button; the glass sits inside it
        // (liquid-glass sets position: relative, which would undo "absolute").
        <div className="absolute right-0 top-full mt-2 z-[120] w-[min(15.5rem,calc(100vw-2rem))]">
        <div
          id={panelId}
          role="group"
          aria-label="Sound settings"
          className={`rounded-2xl p-2 shadow-2xl text-left ${
            isNight ? 'liquid-glass liquid-glass-deep !bg-[rgba(4,16,36,0.85)] text-white' : 'bg-white border border-black/10 text-black'
          }`}
        >
          <p className={`px-2.5 pt-1.5 pb-2 text-[0.625rem] uppercase tracking-widest ${isNight ? 'text-white/55' : 'text-black/50'}`}>Sound</p>
          {ROWS.map(({ key, label, Icon }) => {
            const on = prefs[key];
            return (
              <button
                key={key}
                type="button"
                role="switch"
                aria-checked={on}
                onClick={() => setSoundPrefs({ [key]: !on })}
                className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-sm outline-none focus-visible:ring-2 ${ring} ${
                  isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'
                }`}
              >
                <Icon size={16} className={on ? 'opacity-85' : 'opacity-40'} aria-hidden="true" />
                <span className={`flex-1 text-left ${on ? '' : 'opacity-60'}`}>{label}</span>
                {/* The switch itself */}
                <span
                  aria-hidden="true"
                  className={`relative shrink-0 w-9 h-5 rounded-full transition-colors ${
                    on ? (isNight ? 'bg-white' : 'bg-[#1a1a1a]') : (isNight ? 'bg-white/20' : 'bg-black/15')
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                    on ? `left-[1.125rem] ${isNight ? 'bg-[#0b1830]' : 'bg-white'}` : `left-0.5 ${isNight ? 'bg-white/80' : 'bg-white shadow'}`
                  }`} />
                </span>
              </button>
            );
          })}
        </div>
        </div>
      )}
    </div>
  );
}
